import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { data } from '../formato';
import { Carregando, ErroDaApi, Rn, Selo, Vazio } from '../ui';
import type { Tom } from '../ui';

/**
 * A fila da metrologia.
 *
 * Seis ondas de carga puseram 223 equipamentos, 127 calibrações externas
 * e 17 certificados internos no parque — e junto com eles seis filas de
 * trabalho que, até aqui, só apareciam abrindo ficha por ficha.
 *
 * A tela não calcula nada: a visão `vw_pendencia_metrologia` enumera e a
 * `..._resumo` conta, e é a MESMA fonte (`D-29`). «Quantos certificados
 * faltam?» é pergunta de indicador, de auditoria e de tela; se a tela
 * contasse por conta própria, um dia daria outro número.
 *
 * A ordem das seções não é alfabética: é a da consequência. O que impede
 * ensaio vem antes do que atrasa papel.
 */

interface Pendencia {
  tipo: string;
  gravidade: 'critico' | 'atencao';
  equipamento_id: string | null;
  codigo_patrimonial: string;
  equipamento: string;
  detalhe: string | null;
  referencia: string | null;
  quando: string | null;
}

interface Resumo {
  tipo: string; quantas: number; criticas: number; mais_antiga: string | null;
}

/** Um rascunho e o que ele está segurando (`0107`). */
interface Rascunho {
  certificado_id: string;
  codigo: string;
  titulo: string;
  categoria: string;
  data_afericao: string;
  responsavel: string | null;
  constantes: number;
  equipamentos: number;
  ensaios_parados: number;
  lista_equipamentos: string | null;
}

const TIPOS: Record<string, { nome: string; explica: string; rn?: string }> = {
  calibracao_vencida: {
    nome: 'Calibração vencida ou a vencer',
    explica: 'Equipamento com calibração fora da validade não é oferecido em '
      + 'ensaio nenhum. O que vence nos próximos 60 dias entra aqui como '
      + 'aviso, para a calibração ser marcada antes de o laboratório parar.',
    rn: 'RN-151',
  },
  sem_constante: {
    nome: 'Item calibrado sem constante vigente',
    explica: 'Cápsula sem tara, cilindro sem volume, anel sem altura. É o '
      + 'que o cálculo do ensaio vai procurar e não achar — e a conta que '
      + 'não acha a constante é a conta que alguém completa de cabeça.',
  },
  certificado_em_rascunho: {
    nome: 'Certificado interno em rascunho',
    explica: 'Calibração interna feita que ainda não publicou valor nenhum. Aprovar é '
      + 'o gesto que publica, e a assinatura é de quem clica.',
  },
  certificado_sem_pdf: {
    nome: 'Certificado externo sem o PDF',
    explica: 'O número e a data estão registrados, o documento não. Sem o '
      + 'arquivo a análise crítica não pode ser aprovada.',
    rn: 'RN-155',
  },
  afericao_sem_padrao: {
    nome: 'Valor calibrado sem instrumento declarado',
    explica: 'A rastreabilidade para no certificado e não chega ao número — '
      + 'e é o número que entra na conta.',
  },
  periodicidade_inferida: {
    nome: 'Periodicidade deduzida, e não confirmada',
    explica: 'O intervalo entre calibrações saiu do histórico do próprio '
      + 'equipamento, e não de certificado, norma ou decisão escrita. Ele '
      + 'continua valendo — apagá-lo deixaria o equipamento sem vencimento '
      + 'nenhum, que é pior. O que falta é alguém confirmar que está certo.',
  },
  origem_sem_lastro: {
    nome: 'Origem da periodicidade sem lastro',
    explica: 'A ficha diz que o intervalo veio do certificado, e não há '
      + 'certificado com validade declarada. Origem que aponta para um papel '
      + 'que não existe é pior que origem nenhuma: ela encerra a pergunta '
      + 'com uma resposta falsa.',
  },
  certificado_sem_fornecedor: {
    nome: 'Certificado que não diz quem calibrou',
    explica: 'A 17025 pede que o laboratório avalie e aprove quem presta o '
      + 'serviço (§6.6). Um certificado sem fornecedor não permite responder '
      + '«esse laboratório era aprovado quando calibrou?» — e é essa a '
      + 'pergunta da auditoria. São os certificados que entraram por carga: '
      + 'o número e a data vieram do papel, o nome do laboratório não.',
  },
  certificado_sem_grandeza: {
    nome: 'Certificado que não declara o que foi calibrado',
    explica: 'Força? Deslocamento? Temperatura? A ficha do equipamento '
      + 'agrupa por grandeza para responder «a força está calibrada hoje?», '
      + 'e um certificado sem grandeza não entra em nenhuma resposta — fica '
      + 'num bloco à parte que ninguém consulta antes de um ensaio.',
  },
  curva_sem_regra: {
    nome: 'Curva sem regra de leitura',
    explica: 'A curva está publicada com os pontos medidos, mas como ler '
      + 'entre eles ainda não foi definido. É regra de cálculo, e precisa '
      + 'de validação antes de entrar em ensaio.',
  },
};

/** A ordem da consequência: o que impede ensaio vem antes do que atrasa papel. */
const ORDEM = ['calibracao_vencida', 'sem_constante', 'certificado_em_rascunho',
  'certificado_sem_pdf', 'afericao_sem_padrao', 'curva_sem_regra',
  /* Os dois da `0114` ficam depois do PDF e antes da periodicidade: eles
     não impedem ensaio nenhum hoje, mas são o que uma auditoria pergunta
     primeiro depois de olhar o papel. */
  'certificado_sem_fornecedor', 'certificado_sem_grandeza',
  'periodicidade_inferida', 'origem_sem_lastro'];

const TOM: Record<string, Tom> = { critico: 'critico', atencao: 'atencao' };

export function PendenciasMetrologia() {
  const [resumo, setResumo] = useState<Resumo[] | null>(null);
  const [itens, setItens] = useState<Pendencia[]>([]);
  const [aberto, setAberto] = useState<string | null>(null);
  const [fila, setFila] = useState<Rascunho[]>([]);
  const [erro, setErro] = useState<unknown>(null);

  useEffect(() => {
    api<{ resumo: Resumo[]; itens: Pendencia[] }>('/equipamentos/pendencias')
      .then((r) => { setResumo(r.resumo); setItens(r.itens); setErro(null); })
      .catch(setErro);
    /* A fila de assinatura é uma segunda pergunta, e falha em separado:
       um erro nela não pode apagar as outras cinco filas da tela. */
    api<Rascunho[]>('/equipamentos/certificados/fila-de-assinatura')
      .then(setFila).catch(() => setFila([]));
  }, []);

  const porTipo = (t: string) => itens.filter((i) => i.tipo === t);
  const quantas = (t: string) => resumo?.find((r) => r.tipo === t)?.quantas ?? 0;
  const criticas = resumo?.reduce((s, r) => s + r.criticas, 0) ?? 0;
  const total = resumo?.reduce((s, r) => s + r.quantas, 0) ?? 0;

  return (
    <div>
      <div className="cabeca">
        <h1>Pendências da metrologia</h1>
        <p className="sub">
          O que falta para o parque poder responder por um ensaio
        </p>
      </div>

      <ErroDaApi erro={erro} />

      {resumo === null && !erro && <Carregando texto="Levantando as pendências" />}

      {resumo && total === 0 && (
        <Vazio>
          Nenhuma pendência de metrologia. Todo equipamento tem calibração
          vigente, todo item calibrado tem constante e todo certificado tem
          documento.
        </Vazio>
      )}

      {resumo && total > 0 && (
        <>
          <div className="cartao">
            <div className="cartao-cabeca">
              <h2>Resumo</h2>
              <span className="sub">
                {total} pendência(s)
                {criticas > 0 && ` · ${criticas} que impedem ensaio`}
              </span>
            </div>
            <div className="filtros">
              {ORDEM.filter((t) => quantas(t) > 0).map((t) => (
                <button
                  key={t}
                  type="button"
                  className={`botao ${aberto === t ? '' : 'discreto'}`}
                  onClick={() => setAberto(aberto === t ? null : t)}
                >
                  {TIPOS[t]?.nome ?? t} <span className="sub">{quantas(t)}</span>
                </button>
              ))}
            </div>
          </div>

          <FilaDeAssinatura fila={fila} />

          {ORDEM.filter((t) => quantas(t) > 0)
            .filter((t) => !aberto || aberto === t)
            .map((t) => (
              <div className="cartao" key={t}>
                <div className="cartao-cabeca">
                  <h2>{TIPOS[t]?.nome ?? t}</h2>
                  <span className="sub">{porTipo(t).length} de {quantas(t)}</span>
                </div>
                <p className="sub">
                  {TIPOS[t]?.explica}
                  {TIPOS[t]?.rn && (
                    <> <Rn codigo={TIPOS[t].rn!} diz={TIPOS[t]!.explica} /></>
                  )}
                </p>

                <div className="rola">
                  <table className="dados">
                    <thead>
                      <tr>
                        <th>Equipamento</th>
                        <th className="col-opcional">Detalhe</th>
                        <th className="col-opcional">Referência</th>
                        <th>Desde</th>
                        <th>Situação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {porTipo(t).map((p, i) => (
                        <tr key={`${p.tipo}-${p.codigo_patrimonial}-${p.referencia ?? i}`}>
                          <td>
                            {p.equipamento_id
                              ? (
                                <Link to={`/equipamento/${p.equipamento_id}`}>
                                  <b className="mono">{p.codigo_patrimonial}</b>
                                </Link>
                              )
                              : <b className="mono">{p.codigo_patrimonial}</b>}
                            <div className="sub">{p.equipamento}</div>
                          </td>
                          <td className="col-opcional">
                            {p.detalhe ?? <span className="nada">—</span>}
                          </td>
                          <td className="col-opcional mono">
                            {p.referencia ?? <span className="nada">—</span>}
                          </td>
                          <td>
                            {p.quando ? data(p.quando) : <span className="nada">—</span>}
                          </td>
                          <td>
                            <Selo tom={TOM[p.gravidade] ?? 'neutro'}>
                              {p.gravidade === 'critico' ? 'impede ensaio' : 'atenção'}
                            </Selo>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
        </>
      )}
    </div>
  );
}

/**
 * A fila de assinatura, na ordem em que ela importa.
 *
 * A seção «certificado em rascunho» acima lista os certificados; esta
 * responde a pergunta seguinte, que é a que decide o dia de quem assina:
 * **qual deles destrava mais?**
 *
 * Dezessete rascunhos seguram 340 constantes. Por data, todos pesam
 * igual, e quem tem quinze minutos assina o que está por cima. Aqui
 * vêm primeiro os que estão segurando ENSAIO JÁ LANÇADO — «24
 * constantes» é volume; «3 ensaios sem curva» é gente esperando.
 *
 * ─── Ela não assina ────────────────────────────────────────────────
 *
 * Cada linha leva ao certificado, e é lá que se aprova, um por um, com a
 * prévia do que vai passar a valer na frente (`0105`). Um botão que
 * aprovasse a fila inteira publicaria 340 constantes num gesto que
 * ninguém leu — e aprovar é justamente o gesto que publica (`0094`).
 */
function FilaDeAssinatura({ fila }: { fila: Rascunho[] }) {
  if (fila.length === 0) return null;
  const constantes = fila.reduce((s, r) => s + r.constantes, 0);
  const presos = fila.reduce((s, r) => s + r.ensaios_parados, 0);

  return (
    <div className="cartao">
      <div className="cartao-cabeca">
        <h2>Fila de assinatura</h2>
        <span className="sub">
          {fila.length} rascunho(s) · {constantes} constante(s)
          {presos > 0 && ` · ${presos} ensaio(s) esperando`}
        </span>
      </div>
      <p className="sub">
        Na ordem da consequência, e não da data: primeiro o que está
        segurando ensaio já lançado. Aprovar é o gesto que publica a
        constante — e se faz um certificado por vez, com a prévia do que
        vai passar a valer na frente.
      </p>

      <div className="rola">
        <table className="dados">
          <thead>
            <tr>
              <th>Certificado</th>
              <th className="col-opcional">Equipamentos</th>
              <th className="num">Constantes</th>
              <th className="num">Ensaios parados</th>
              <th className="col-opcional">Calibrado em</th>
            </tr>
          </thead>
          <tbody>
            {fila.map((r) => (
              <tr key={r.certificado_id}>
                <td>
                  <Link to={`/equipamentos/verificacoes/${r.certificado_id}`}>
                    <b className="mono">{r.codigo}</b>
                  </Link>
                  <div className="sub">{r.titulo}</div>
                </td>
                <td className="col-opcional">
                  {r.equipamentos > 0
                    ? (
                      <>
                        <b>{r.equipamentos}</b>
                        <div className="sub mono">{r.lista_equipamentos}</div>
                      </>
                    )
                    : <span className="nada">—</span>}
                </td>
                <td className="num">{r.constantes}</td>
                <td className="num">
                  {r.ensaios_parados > 0
                    ? <Selo tom="critico">{r.ensaios_parados}</Selo>
                    : <span className="nada">—</span>}
                </td>
                <td className="col-opcional">{data(r.data_afericao)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
